import { rem } from 'polished'
import { useCallback, useEffect, useReducer, useRef } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import clsx from 'clsx'

import zIndex from 'styles/tools/z-index'
import { useElementHeight } from 'hooks/useElementSize'
import { getHeight } from 'lib/window/getViewport'
import eases from 'styles/tools/eases'
import { StyledModalBody } from 'LuxKit/Modal/ModalBody'
import { StyledModalFooter } from 'LuxKit/Modal/ModalFooter'
import { StyledModalHeader } from 'LuxKit/Modal/ModalHeader'
import { MODAL_HEADER_GRID_AREA, MODAL_BODY_GRID_AREA, MODAL_FOOTER_GRID_AREA } from 'LuxKit/Modal/ModalSheet'
import { clamp } from 'lib/maths/mathUtils'
import useStateWithRef from 'hooks/useStateWithRef'
import noop from 'lib/function/noop'
import useAutoUpdatingRef from 'hooks/useAutoUpdatingRef'
import PreviewableModalBackdrop from './PreviewableModalBackdrop'
import Clickable from 'components/Common/Clickable'
import { LuxKitModalBehaviourDispatchContext, LuxKitModalBehaviourStateContext } from 'LuxKit/Modal/context/LuxKitModalBehaviourContexts'
import type { LuxKitModalBehaviourDispatchAction } from 'LuxKit/Modal/context/LuxKitModalBehaviourState'
import type LuxKitModalBehaviourState from 'LuxKit/Modal/context/LuxKitModalBehaviourState'
import { luxKitModalBehaviourStateReducer, MODAL_BEHAVIOUR_INITIAL_STATE } from 'LuxKit/Modal/context/LuxKitModalBehaviourState'
import ScrollLock from 'components/Common/ScrollLock'

const SWIPE_HANDLE_AREA = 'swipe-handle'

const PREVIEW_HEIGHT = 150
const TOP_GAP_HEIGHT = 72
const SWIPE_VELOCITY_THRESHOLD = 0.25

const PreviewableModalRoot = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  z-index: ${zIndex.modal};
  max-height: calc(100dvh - ${TOP_GAP_HEIGHT}px);
  background-color: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: ${props => props.theme.shadow.bottom.medium};
  border-radius: ${props => props.theme.borderRadius.L} ${props => props.theme.borderRadius.L} 0 0;
  overflow: hidden;

  display: grid;
  grid-template-areas:
    '${SWIPE_HANDLE_AREA}'
    '${MODAL_HEADER_GRID_AREA}'
    '${MODAL_BODY_GRID_AREA}'
    '${MODAL_FOOTER_GRID_AREA}';
  grid-template-rows:
    [first]
    min-content
    min-content
    1fr
    min-content
    [last];
  grid-template-columns: [first] 1fr [last];

  > ${StyledModalHeader} {
    grid-area: ${MODAL_HEADER_GRID_AREA};
  }

  > ${StyledModalBody} {
    grid-area: ${MODAL_BODY_GRID_AREA};
  }

  > ${StyledModalFooter} {
    grid-area: ${MODAL_FOOTER_GRID_AREA};
  }
`

const SwipeHandle = styled(Clickable)`
  position: absolute;
  top: 0;
  left: ${rem(60)};
  right: ${rem(60)};
  height: ${rem(50)};
  grid-area: ${SWIPE_HANDLE_AREA};
  z-index: 2;
`

const SwipeHandleVisual = styled.div`
  padding-top: ${rem(12)};
  display: flex;
  justify-content: center;

  &::after {
    content: '';
    height: ${rem(3)};
    width: ${rem(56)};
    background-color: ${props => props.theme.palette.neutral.default.five};
  }
`

function getTouch(touches: TouchList, identifier: number) {
  return Array.from(touches).find(t => t.identifier === identifier)
}

interface Props {
  onRequestClose?: () => void
}

function PreviewableModal({ children, onRequestClose }: React.PropsWithChildren<Props>) {
  const handleRef = useRef<HTMLButtonElement>(null)
  const viewportHeight = getHeight()
  const cardRef = useRef<HTMLDivElement>(null)
  const height = useElementHeight(cardRef)

  const [isSwiping, setIsSwiping, isSwipingRef] = useStateWithRef(false)
  const [isOpen, setIsOpen, isOpenRef] = useStateWithRef(false)

  const maxHeight = Math.max(Math.min(viewportHeight - TOP_GAP_HEIGHT, height ?? PREVIEW_HEIGHT), PREVIEW_HEIGHT)
  const _expandedOffset = maxHeight - PREVIEW_HEIGHT
  // Use expandedOffset via ref to avoid re-running the effect, regenerating callbacks etc.
  const expandedOffset = useAutoUpdatingRef(_expandedOffset)

  const resetPosition = useCallback((isOpen: boolean = isOpenRef.current) => {
    const card = cardRef.current
    if (card) {
      card.style.transition = `transform 0.3s ${eases.power2.out}`
      if (isOpen) {
        card.style.transform = `translateY(-${expandedOffset.current}px)`
      } else {
        card.style.transform = 'translateY(0)'
      }
    }
  }, [cardRef, expandedOffset, isOpenRef])

  useEffect(() => {
    if (!isSwipingRef.current) {
      resetPosition()
    }
  }, [_expandedOffset, resetPosition, isSwipingRef])

  // Use setOpen via ref to avoid re-running the effect
  const setOpen = useAutoUpdatingRef(
    useCallback((value: boolean) => {
      setIsOpen(value)
      resetPosition(value)
    }, [resetPosition, setIsOpen]),
  )

  const clearSwipeState = useRef<() => void>(noop)

  useEffect(() => {
    const card = cardRef.current

    if (card) {
      const onTouchStart = (e: TouchEvent) => {
        if (isSwipingRef.current || e.target !== handleRef.current) return false

        const wasOpen = isOpenRef.current
        const touchID = e.touches[0].identifier
        const startY = e.touches[0].clientY
        let currentY = startY
        let prevTime = Date.now()
        let prevY = startY
        let velocity = 0 // Velocity in pixels per millisecond

        function getDiff(touch: Touch) {
          currentY = touch.clientY
          const diffRelativeToStart = startY - currentY
          const diffFromBottom = wasOpen ? expandedOffset.current + diffRelativeToStart : diffRelativeToStart
          return clamp(0, diffFromBottom, expandedOffset.current)
        }

        function updateVelocity() {
          const currentTime = Date.now()
          const timeDiff = currentTime - prevTime
          if (timeDiff <= 0) return

          const frameDiff = currentY - prevY
          const instantaneousVelocity = frameDiff / timeDiff

          // Smooth out the velocity to account for touchscreen jitter
          const SMOOTHING_MS = 80 // Higher = more smoothing
          const updateWeight = Math.min(1, timeDiff / SMOOTHING_MS)
          velocity = updateWeight * instantaneousVelocity + (1 - updateWeight) * velocity

          prevTime = currentTime
          prevY = currentY
        }

        const velocityMeasuringInterval = setInterval(updateVelocity, 32)

        const onTouchMove = (e: TouchEvent) => {
          const touch = getTouch(e.touches, touchID)
          if (!touch || !isSwipingRef.current) return false
          e.preventDefault()

          const diff = getDiff(touch)
          updateVelocity()

          card.style.transition = 'none'
          card.style.transform = `translateY(-${diff}px)`
        }

        const onTouchEnd = (e: TouchEvent) => {
          const touch = getTouch(e.changedTouches, touchID)
          if (!touch) return false

          const diff = getDiff(touch)

          if (velocity > SWIPE_VELOCITY_THRESHOLD) {
            setOpen.current(false)
          } else if (velocity < -SWIPE_VELOCITY_THRESHOLD) {
            setOpen.current(true)
          } else {
            setOpen.current(diff > expandedOffset.current / 2)
          }

          clearSwipeState.current()
        }

        card.addEventListener('touchmove', onTouchMove, { passive: false })
        card.addEventListener('touchend', onTouchEnd, { passive: false })

        clearSwipeState.current = () => {
          card.removeEventListener('touchmove', onTouchMove)
          card.removeEventListener('touchend', onTouchEnd)
          clearInterval(velocityMeasuringInterval)
          setIsSwiping(false)
          clearSwipeState.current = noop
        }

        setIsSwiping(true)

        return false
      }

      card.addEventListener('touchstart', onTouchStart, { passive: false })
      return () => {
        card.removeEventListener('touchstart', onTouchStart)
        clearSwipeState.current()
        resetPosition()
      }
    }
  }, [cardRef, expandedOffset, isOpenRef, isSwipingRef, setIsSwiping, setOpen, handleRef, resetPosition])

  const [state, dispatch] = useReducer<LuxKitModalBehaviourState, [LuxKitModalBehaviourDispatchAction]>(luxKitModalBehaviourStateReducer, MODAL_BEHAVIOUR_INITIAL_STATE)

  return (
    <>
      <PreviewableModalBackdrop isVisible={isOpen} onClick={onRequestClose ?? (() => setOpen.current(false))} />
      <PreviewableModalRoot
        ref={cardRef}
        style={{
          top: `calc(100dvh - min(${PREVIEW_HEIGHT}px, ${height}px))`,
        }}
        className={clsx({ isSwiping })}
      >
        <SwipeHandle
          ref={handleRef}
          onClick={() => {
            setOpen.current(!isOpen)
            clearSwipeState.current()
          }}
        />
        <SwipeHandleVisual />
        <LuxKitModalBehaviourStateContext.Provider value={state}>
          <LuxKitModalBehaviourDispatchContext.Provider value={dispatch}>
            {children}
          </LuxKitModalBehaviourDispatchContext.Provider>
        </LuxKitModalBehaviourStateContext.Provider>
      </PreviewableModalRoot>
      <ScrollLock isActive={isOpen} />
    </>
  )
}

export default PreviewableModal
